"use client";

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { ChevronDownIcon } from "@/components/ui/chevron-down";
import { cn } from "@/lib/utils";

/**
 * Height, in px, a user message bubble may reach before it gets clipped behind
 * a "Show more" toggle — roughly ten lines of body text plus bubble padding.
 */
const COLLAPSED_MAX_HEIGHT = 300;

/**
 * A bubble only slightly over the limit isn't worth a toggle: hiding two stray
 * lines costs the reader more than the reclaimed space is worth.
 */
const OVERFLOW_TOLERANCE = 48;

/**
 * Width, in px, the bubble is held to while a toggle is showing. Bubbles are
 * `w-fit`, and the collapsed toggle floats out of flow, so a tall-but-narrow
 * message (a bare list of numbers, say) would otherwise clip its own button.
 */
const TOGGLE_MIN_WIDTH = 160;

export type CollapsibleMessage = {
  /** Spread onto the element that should be clipped (the message bubble). */
  bubbleProps: {
    id: string;
    ref: React.RefObject<HTMLDivElement | null>;
    style: React.CSSProperties | undefined;
  };
  /** True once the content is tall enough to warrant a toggle. */
  showToggle: boolean;
  isExpanded: boolean;
  toggle: () => void;
  regionId: string;
};

/**
 * Clips overly tall message bubbles behind a Show more / Show less toggle.
 * Pass `enabled: false` for assistant messages, which are never collapsed.
 */
export function useCollapsibleMessage(enabled: boolean): CollapsibleMessage {
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const regionId = useId();
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const restoreOnCollapseRef = useRef(false);

  useEffect(() => {
    const el = bubbleRef.current;
    if (!enabled || !el) return;
    let cancelled = false;
    // scrollHeight reports the full content height whether or not the bubble is
    // clamped, so the measurement stays stable across toggles. ResizeObserver
    // fires once on observe, which covers the initial measurement too.
    const measure = () => {
      if (cancelled) return;
      setIsOverflowing(el.scrollHeight > COLLAPSED_MAX_HEIGHT + OVERFLOW_TOLERANCE);
    };
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    // A clamped bubble's own box stops changing size, so the observer can't see
    // text reflowing behind the clamp when a webfont swaps in. Re-measure once
    // fonts settle, otherwise a borderline message stays clipped with no toggle.
    document.fonts?.ready.then(measure);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [enabled]);

  const toggle = useCallback(() => {
    restoreOnCollapseRef.current = isExpanded;
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  // Collapsing a bubble the reader has scrolled past would drop them into
  // unrelated content further down the thread — pull its top back into view.
  // `block: "nearest"` is a no-op when the bubble is already fully visible.
  useLayoutEffect(() => {
    if (isExpanded || !restoreOnCollapseRef.current) return;
    restoreOnCollapseRef.current = false;
    bubbleRef.current?.scrollIntoView({ behavior: "instant", block: "nearest" });
  }, [isExpanded]);

  const showToggle = enabled && isOverflowing;

  return {
    bubbleProps: {
      id: regionId,
      ref: bubbleRef,
      style:
        enabled && (!isExpanded || showToggle)
          ? {
              // Clamp from the first paint rather than after measuring, so a
              // long message never renders at full height and then snaps shut.
              maxHeight: isExpanded ? undefined : COLLAPSED_MAX_HEIGHT,
              minWidth: showToggle ? TOGGLE_MIN_WIDTH : undefined,
            }
          : undefined,
    },
    isExpanded,
    regionId,
    showToggle,
    toggle,
  };
}

export type MessageCollapseToggleProps = {
  collapse: CollapsibleMessage;
};

/**
 * Render as the last child of the clipped bubble. While collapsed it floats
 * over the cut edge on a fade; once expanded it sits inline below the text.
 */
export const MessageCollapseToggle = ({ collapse }: MessageCollapseToggleProps) => {
  const { isExpanded, regionId, toggle } = collapse;

  const button = (
    <Button
      aria-controls={regionId}
      aria-expanded={isExpanded}
      className="pointer-events-auto text-muted-foreground"
      onClick={toggle}
      size="xs"
      type="button"
      variant="ghost"
    >
      {isExpanded ? "Show less" : "Show more"}
      <ChevronDownIcon
        aria-hidden="true"
        className={cn("flex transition-transform", isExpanded && "rotate-180")}
        size={12}
      />
    </Button>
  );

  if (isExpanded) {
    return <div className="flex justify-start">{button}</div>;
  }

  return (
    // The horizontal/bottom offsets mirror MessageContent's own bubble padding
    // so the button lines up with the text it is hiding.
    <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-start bg-linear-to-t from-secondary from-40% to-transparent px-4 pt-10 pb-3">
      {button}
    </div>
  );
};
